import { useState, useEffect, useRef } from 'react';
import { Flex, Box, Stack, Grid } from '@chakra-ui/react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { editUserProfile, uploadProfilePicture } from '../actions/users-profile-actions';
import UserProfileImageDisplay from '../../global/components/helper-components/components/UserProfileImageDisplay';
import UserProfileImageUploadButton from '../../global/components/helper-components/components/UserProfileImageUploadButton';
import { resizeImage } from '../../global/helpers/global-helpers';
import LaunchdeckButton from '../../global/components/helper-components/components/LaunchdeckButton';
import { getTimestampForFileName } from '../../global/helpers/time-format-helpers';
import LaunchdeckFormInput from '../../forms/TextInputField/components/LaunchdeckFormInput';


function UserEditPersonalInfoForm({
  userAccountProfile,
  handleCloseModal,
  // Action
  editUserProfile,
  uploadProfilePicture,
  // Redux States
  uploadProfilePictureResults,
  uploadProfilePictureInProgress,
  userProfilePicture
}) {

  const initRef = useRef(false);

  const [localState, setLocalState] = useState({
    isEditMode: false,
    isSetupMode: false,
    isLoading: true,
    displayValidationError: false,
    imageIsLoading: true,
    firstName: "",
    lastName: "",
    email: "",
    phoneCountryCode: "",
    phoneAreaCode: "",
    phoneNumber: "",
    meetingLink: ""
  })
  const [profilePicDisplay, setProfilePicDisplay] = useState(userProfilePicture)
  const [clickCount, setClickCount] = useState(0)

  useEffect(() => {
    if (userAccountProfile && !initRef.current) {
      const {
        firstName,
        lastName,
        email,
        meetingLink,
        phoneCountryCode,
        phoneAreaCode,
        phoneNumber
      } = userAccountProfile || {}

      setLocalState(prevState => ({
        ...prevState,
        ...firstName && { firstName },
        ...lastName && { lastName },
        ...email && { email },
        ...meetingLink && { meetingLink },
        ...phoneCountryCode && { phoneCountryCode },
        ...phoneAreaCode && { phoneAreaCode },
        ...phoneNumber && { phoneNumber }
      }));

      initRef.current = true
    }
  }, [userAccountProfile])


  function handleChange(event) {
    const value = event.target.value;

    setLocalState(prevState => ({
      ...prevState,
      [event.target.name]: value
    }));
  }

  const handleSubmitEditProfile = async () => {
    const { firstName, lastName, email, phoneCountryCode, phoneAreaCode, phoneNumber, meetingLink } = localState;
    const allRequiredFieldsFilled = (firstName && lastName && email) ? true : false;

    if (allRequiredFieldsFilled === true) {
      try {
        setLocalState(prevState => ({
          ...prevState,
          displayValidationError: false,
        }));

        const updatedUserParams = {
          firstName: firstName ? firstName.trim() : null,
          lastName: lastName ? lastName.trim() : null,
          ...phoneCountryCode && { phoneCountryCode },
          ...phoneAreaCode && { phoneAreaCode },
          ...phoneNumber && { phoneNumber },
          ...meetingLink && { meetingLink }
          // Do not allow change of email here
        }

        await editUserProfile(updatedUserParams);

        handleCloseModal()
      } catch (error) {
        console.log('Update User Profile Error', error);
      }

    } else {
      setLocalState(prevState => ({
        ...prevState,
        displayValidationError: true
      }));
    }
  }


  const onChangeUploadPicture = async (event) => {
    let providedImageFile = event.target.files[0];
    let { name, size } = providedImageFile || {}
    let resizedFile = '';
    let imageFileSizeLimit = 20000;
    const resizeNeeded = size > imageFileSizeLimit ? true : false;

    if (resizeNeeded) {
      let resizedImage = await resizeImage(event.target.files[0]);
      let newFileName = '100x100-' + name;
      resizedFile = new File([resizedImage], newFileName, { type: "image/png" })
    }

    let uploadedPicture = resizeNeeded ? resizedFile : providedImageFile;

    setLocalState(prevState => ({
      ...prevState,
      uploadedPicture
    }));
  };


  useEffect(() => {
    if (localState.uploadedPicture) {
      uploadProfilePicture(localState.uploadedPicture);
    }
  }, [localState.uploadedPicture, uploadProfilePicture])


  useEffect(() => {
    if (Object.keys(uploadProfilePictureResults).length !== 0) {
      setLocalState(prevState =>
      ({
        ...prevState,
        uploadedPicture: ""
      }))

      setProfilePicDisplay(userProfilePicture ? `${userProfilePicture}?t=${getTimestampForFileName()}` : "");
      setClickCount(prevCount => prevCount + 1)
    }

  }, [uploadProfilePictureResults, userProfilePicture])

  const { fullName, firstName, lastName, email,
    phoneCountryCode, phoneAreaCode, phoneNumber, meetingLink,
    displayValidationError } = localState || {};


  return (
    <Flex align='center' w='100%' direction='column'>
      <Stack w='100%' maxW={['inherit','35em']} px={['1.5em', '2em']} py='1em' overflowY='auto' maxHeight='75vh'>
        <Flex w='100%' justify='center' align='center' mt='0.5em' mb='1.5em'>
          <Box position='relative'>
            <UserProfileImageDisplay
              key={`user-edit-profile-pic-${clickCount}`}
              name={fullName}
              size='5em'
              profilePicture={profilePicDisplay}
              uploadProfilePictureInProgress={uploadProfilePictureInProgress}

            />

            <UserProfileImageUploadButton onChangeUploadPicture={onChangeUploadPicture} uploadProfilePictureInProgress={uploadProfilePictureInProgress} />

          </Box>
        </Flex>

        <Flex direction='column' w='100%' pb='0.7em'>
          <LaunchdeckFormInput
            name={'edit-profile-email-input'}
            value={email}
            isDisabled={true}
          />
        </Flex>

        <Grid w='100%' pb='0.7em' templateColumns={["1fr", "1fr 1fr"]} templateRows={["1fr 1fr", "1fr"]} columnGap="0.5em">
          <Flex w='100%'>
            <LaunchdeckFormInput
              label='First name'
              name={'firstName'}
              onChange={handleChange}
              value={firstName}
              isRequired={true}

              errorLogic={displayValidationError && !firstName}
              errorMessage="Required"
            />
          </Flex>
          <Flex w='100%'>
            <LaunchdeckFormInput
              label='Last name(s)'
              name={'lastName'}
              onChange={handleChange}
              value={lastName}
              isRequired={true}

              errorLogic={displayValidationError && !lastName}
              errorMessage="Required"
            />
          </Flex>
        </Grid>

        <Box className='section-header-label' color='brandDark.500' fontSize={['0.8em', '0.875em']}>Contact</Box>
        <Box mt='-0.5em' fontSize='0.75em' color='gray.500' fontStyle='italic'>Let your clients know how to get in touch directly</Box>

        <Flex w='100%' mt='1em' gap="0.5em" direction={['column', 'row']}>
          <Flex flex='2' gap="0.5em">
            <Box flex='1'>
              <LaunchdeckFormInput
                label='Country'
                name={'phoneCountryCode'}
                value={phoneCountryCode}
                onChange={handleChange}
              />
            </Box>
            <Box flex='1'>
              <LaunchdeckFormInput
                label='Area'
                name={'phoneAreaCode'}
                value={phoneAreaCode}
                onChange={handleChange}
              />
            </Box>
          </Flex>
          <Box flex='6'>
            <LaunchdeckFormInput
              label='Phone number'
              name={'phoneNumber'}
              value={phoneNumber}
              onChange={handleChange}
            />
          </Box>
        </Flex>


        <Flex direction='column' w='100%' mt='0.5em'>
          <LaunchdeckFormInput
            label='Book-a-meeting link'
            name={'meetingLink'}
            value={meetingLink}
            onChange={handleChange}
          />
          {/* <Box fontSize='0.75em' mt='0em' pl='0.5em' color='gray.500' fontStyle='italic'>Your private Calendly or Google meet link for your clients to contact you </Box> */}
        </Flex>

      
      </Stack>

      <Flex w='100%' p={['1em 2em 1em 2em', '2em']} maxW={['inherit','35em']}>
        <Flex justify='center' w='100%'>
          <LaunchdeckButton bg='brand.500' color='gray.50' w='100%' label='Save changes' onClick={() => handleSubmitEditProfile()} />
        </Flex>
      </Flex>


    </Flex>

  );

}


function mapStateToProps(state) {
  return {
    userAccountProfile: state.userProfile.accountUserProfile,
    uploadProfilePictureResults: state.uploadProfilePicture.results,
    uploadProfilePictureInProgress: state.uploadProfilePicture.isLoading,
    userProfilePicture: state.thisUserProfilePicture.result
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      editUserProfile,
      uploadProfilePicture
    },
    dispatch,
  )

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UserEditPersonalInfoForm));